import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { DollarSign, FileText, Star } from 'lucide-react';
import { formatDateTime } from '@/lib/i18n';
import { api } from '@/lib/api';
import { useConfirm } from '@/components/ui/confirm';
import { usePermission } from '@/Hooks/usePermission';
import { Badge, toneForStatus } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Field, Textarea } from '@/components/ui/field';
import { Icon } from '@/components/ui/icon';
import { EmptyState, ErrorState, LoadingState } from '@/components/ui/states';
import { apiError, type EmployeeProfile } from './lib';
import CompensationPanel from './CompensationPanel';

/**
 * One employee's profile. Deliberately DOES NOT fetch or render pay.
 *
 * ADR-0008 §4 — "a manager must not automatically gain compensation
 * visibility" is the single most likely thing in this epic to get wrong, and
 * it gets wrong the same way every time: a screen widens what it asks for.
 * `GET /employees/:id` never carries a rate (`EMPLOYEE_SELECT` on the
 * backend is an allow-list), so there is nothing to leak by accident here —
 * and the fix stays that way on purpose: `CompensationPanel` below is a
 * SEPARATE component making a SEPARATE request to a SEPARATE route
 * (`compensation.view`), mounted only when that permission holds. A future
 * edit to this file cannot make pay ride out on the profile fetch, because
 * the profile fetch was never asked for it.
 */
export default function EmployeeDetail({
  employeeId,
  onChanged,
}: {
  employeeId: string;
  onChanged: () => void;
}) {
  const { t, i18n } = useTranslation();
  const { can } = usePermission();
  const confirm = useConfirm().confirm;
  const canUpdate = can('employees.update');
  const canDeactivate = can('employees.deactivate');
  const canSeeCompensation = can('compensation.view');

  const [profile, setProfile] = useState<EmployeeProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [noteDraft, setNoteDraft] = useState('');
  const [savingNote, setSavingNote] = useState(false);
  const [terminating, setTerminating] = useState(false);
  const [showCompensation, setShowCompensation] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    setError(null);
    api
      .get<EmployeeProfile>(`/employees/${employeeId}`)
      .then(({ data }) => {
        setProfile(data);
        setNoteDraft(data.notes ?? '');
      })
      .catch((e) => setError(apiError(e, t('workforce.employees.loadFailed'))))
      .finally(() => setLoading(false));
  }, [employeeId, t]);

  useEffect(load, [load]);
  useEffect(() => setShowCompensation(false), [employeeId]);

  const saveNote = async () => {
    setSavingNote(true);
    try {
      await api.patch(`/employees/${employeeId}`, { notes: noteDraft });
      toast.success(t('workforce.employees.saved'));
      load();
      onChanged();
    } catch (e) {
      toast.error(apiError(e, t('workforce.employees.saveFailed')));
    } finally {
      setSavingNote(false);
    }
  };

  const terminate = async () => {
    const ok = await confirm({
      title: t('workforce.employees.terminateConfirmTitle'),
      body: t('workforce.employees.terminateConfirmBody'),
      confirmLabel: t('workforce.employees.terminate'),
      tone: 'danger',
    });
    if (!ok) return;
    setTerminating(true);
    try {
      await api.post(`/employees/${employeeId}/terminate`, {
        terminationDate: new Date().toISOString().slice(0, 10),
        reason: t('workforce.employees.terminatedFromDashboard'),
      });
      toast.success(t('workforce.employees.terminated'));
      load();
      onChanged();
    } catch (e) {
      toast.error(apiError(e, t('workforce.employees.terminateFailed')));
    } finally {
      setTerminating(false);
    }
  };

  if (loading) return <LoadingState label={t('common.loading')} />;
  if (error) return <ErrorState message={error} onRetry={load} />;
  if (!profile) return <EmptyState title={t('workforce.employees.notFound')} />;

  const noteChanged = noteDraft !== (profile.notes ?? '');

  return (
    <div className="space-y-4">
      <Card>
        <CardContent className="flex flex-wrap items-center justify-between gap-4 p-5">
          <div>
            <div className="flex flex-wrap items-center gap-2">
              <h2 className="text-lg font-semibold">
                {profile.firstName} {profile.lastName ?? ''}
              </h2>
              <Badge tone={toneForStatus(profile.status)}>{t(`workforce.employmentStatus.${profile.status}`)}</Badge>
            </div>
            <p className="text-xs text-ink-muted">
              {profile.employeeCode} · {profile.jobTitle ?? t('workforce.employees.noJobTitle')}
            </p>
          </div>
          {canDeactivate && profile.status !== 'TERMINATED' && (
            <Button variant="danger" size="sm" disabled={terminating} onClick={() => void terminate()}>
              {t('workforce.employees.terminate')}
            </Button>
          )}
        </CardContent>
      </Card>

      <div className="grid gap-4 lg:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle>{t('workforce.employees.contact')}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2 text-sm">
            <ContactRow label={t('workforce.employees.email')} value={profile.email} />
            <ContactRow label={t('workforce.employees.phone')} value={profile.phone} ltr />
            <ContactRow
              label={t('workforce.employees.hireDate')}
              value={profile.hireDate ? formatDateTime(profile.hireDate, i18n.language) : null}
            />
            <ContactRow
              label={t('workforce.employees.emergencyContact')}
              value={
                profile.emergencyContactName
                  ? `${profile.emergencyContactName} (${profile.emergencyContactRelation ?? '—'}) · ${profile.emergencyContactPhone ?? '—'}`
                  : null
              }
            />
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Icon as={FileText} size="sm" />
              {t('workforce.employees.notes')}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            <Field label={t('workforce.employees.notes')} hint={t('workforce.employees.notesHint')}>
              <Textarea
                rows={4}
                value={noteDraft}
                disabled={!canUpdate}
                onChange={(e) => setNoteDraft(e.target.value)}
              />
            </Field>
            {canUpdate && noteChanged && (
              <div className="flex justify-end gap-2">
                <Button variant="ghost" size="sm" onClick={() => setNoteDraft(profile.notes ?? '')}>
                  {t('common.cancel')}
                </Button>
                <Button size="sm" disabled={savingNote} onClick={() => void saveNote()}>
                  {t('common.save')}
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Icon as={Star} size="sm" />
            {t('workforce.employees.skillsAndDocuments')}
          </CardTitle>
        </CardHeader>
        <CardContent className="grid gap-4 sm:grid-cols-2">
          <div>
            <p className="mb-1.5 text-xs font-semibold uppercase tracking-wide rtl:tracking-normal text-ink-muted">
              {t('workforce.employees.skills')}
            </p>
            {profile.skills.length === 0 ? (
              <p className="text-sm text-ink-muted">{t('workforce.employees.noSkills')}</p>
            ) : (
              <ul className="space-y-1 text-sm">
                {profile.skills.map((skill) => (
                  <li key={skill.id} className="flex items-center justify-between">
                    <span>{skill.name}</span>
                    {skill.level && <span className="tabular-nums text-ink-muted">{skill.level}/5</span>}
                  </li>
                ))}
              </ul>
            )}
          </div>
          <div>
            <p className="mb-1.5 text-xs font-semibold uppercase tracking-wide rtl:tracking-normal text-ink-muted">
              {t('workforce.employees.documents')}
            </p>
            {profile.documents.length === 0 ? (
              <p className="text-sm text-ink-muted">{t('workforce.employees.noDocuments')}</p>
            ) : (
              <ul className="space-y-1 text-sm">
                {profile.documents.map((doc) => (
                  <li key={doc.id} className="flex items-center justify-between gap-2">
                    <span className="truncate">{doc.name}</span>
                    {doc.expiresAt && (
                      <span className="whitespace-nowrap text-xs text-ink-muted">
                        {t('workforce.employees.expires', { date: formatDateTime(doc.expiresAt, i18n.language) })}
                      </span>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Compensation lives behind its own key and its own request, never
          folded into the profile above — the split IS the safeguard. */}
      {canSeeCompensation && (
        <Card>
          <CardHeader className="flex-row items-center justify-between gap-2">
            <CardTitle className="flex items-center gap-2">
              <Icon as={DollarSign} size="sm" />
              {t('workforce.compensation.title')}
            </CardTitle>
            {!showCompensation && (
              <Button variant="outline" size="sm" onClick={() => setShowCompensation(true)}>
                {t('workforce.compensation.reveal')}
              </Button>
            )}
          </CardHeader>
          {showCompensation && (
            <CardContent>
              <CompensationPanel employeeId={employeeId} />
            </CardContent>
          )}
        </Card>
      )}
    </div>
  );
}

function ContactRow({ label, value, ltr }: { label: string; value: string | null; ltr?: boolean }) {
  return (
    <div>
      <p className="text-xs uppercase tracking-wide rtl:tracking-normal text-ink-muted">{label}</p>
      <p dir={ltr && value ? 'ltr' : undefined} className={ltr ? 'text-start' : undefined}>
        {value || '—'}
      </p>
    </div>
  );
}
